import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Star, Send, CheckCircle } from 'lucide-react';
import { PRICING } from '../data/pricing';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, onRate, interactive = true }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        disabled={!interactive}
        onClick={() => interactive && onRate(star)}
        className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
        data-testid={`star-${star}`}
      >
        <Star
          className={`h-6 w-6 ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
        />
      </button>
    ))}
  </div>
);

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    rating: 0,
    serviceType: '',
    text: ''
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews?status=approved`);
      setReviews(res.data);
    } catch {
      console.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success('Thank you for your review!', { description: 'It will be published after approval.' });
      setSubmitted(true);
      setForm({ name: '', email: '', rating: 0, serviceType: '', text: '' });
    } catch {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div>
      <SEO
        title="Customer Reviews"
        description="Read what our customers say about Serene Sparkle cleaning services. Share your own experience!"
        keywords="cleaning service reviews, Serene Sparkle reviews, cleaning testimonials"
        canonical="/reviews"
      />
      <section className="bg-gradient-to-br from-sky-50 to-emerald-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Customer Reviews</h1>
            {reviews.length > 0 && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <StarRating rating={Math.round(parseFloat(avgRating))} interactive={false} onRate={() => {}} />
                <span className="text-lg font-semibold text-slate-700">{avgRating}</span>
                <span className="text-slate-500">({reviews.length} reviews)</span>
              </div>
            )}
            <p className="text-base sm:text-lg text-slate-600">
              See what our customers say about our cleaning services
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Reviews List */}
            <div className="lg:col-span-3">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Recent Reviews</h2>
              {loading ? (
                <p className="text-slate-500">Loading reviews...</p>
              ) : reviews.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center">
                    <Star className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-600">No reviews yet. Be the first to share your experience!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="reviews-list">
                  {reviews.map((review) => (
                    <Card key={review.id} className="border-slate-200" data-testid={`review-${review.id}`}>
                      <CardContent className="py-5">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-slate-900">{review.name}</p>
                            <p className="text-xs text-slate-400 capitalize">{review.serviceType?.replace('-', ' ')}</p>
                          </div>
                          <StarRating rating={review.rating} interactive={false} onRate={() => {}} />
                        </div>
                        <p className="text-sm text-slate-600 mt-2">{review.text}</p>
                        <p className="text-xs text-slate-400 mt-3">
                          {new Date(review.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Review Form */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Leave a Review</h2>
              {submitted ? (
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardContent className="py-8 text-center">
                    <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
                    <p className="font-semibold text-slate-900 mb-1">Thank you!</p>
                    <p className="text-sm text-slate-600 mb-4">Your review has been submitted and will appear after approval.</p>
                    <Button onClick={() => setSubmitted(false)} variant="outline" size="sm" data-testid="write-another-btn">
                      Write another review
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-sky-100" data-testid="review-form">
                  <CardContent className="pt-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="reviewName">Your Name *</Label>
                        <Input
                          id="reviewName"
                          value={form.name}
                          onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                          required
                          placeholder="Jane Smith"
                          className="mt-1"
                          data-testid="review-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="reviewEmail">Email *</Label>
                        <Input
                          id="reviewEmail"
                          type="email"
                          value={form.email}
                          onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                          required
                          placeholder="jane@example.com"
                          className="mt-1"
                          data-testid="review-email"
                        />
                      </div>
                      <div>
                        <Label>Service Used *</Label>
                        <Select onValueChange={(v) => setForm(f => ({ ...f, serviceType: v }))} value={form.serviceType}>
                          <SelectTrigger className="mt-1" data-testid="review-service">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(PRICING).map(([key, cat]) => (
                              <SelectGroup key={key}>
                                <SelectLabel className="text-sky-700 font-semibold">{cat.label}</SelectLabel>
                                {cat.services.map(s => (
                                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Rating *</Label>
                        <div className="mt-1">
                          <StarRating rating={form.rating} onRate={(r) => setForm(f => ({ ...f, rating: r }))} />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="reviewText">Your Review *</Label>
                        <Textarea
                          id="reviewText"
                          value={form.text}
                          onChange={(e) => setForm(f => ({ ...f, text: e.target.value }))}
                          required
                          rows={4}
                          placeholder="Tell us about your experience..."
                          className="mt-1"
                          data-testid="review-text"
                        />
                      </div>
                      <Button type="submit" disabled={submitting} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="submit-review-btn">
                        <Send className="h-4 w-4 mr-2" />
                        {submitting ? 'Submitting...' : 'Submit Review'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
